import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Mountain, Trees, ShieldCheck, Eye, Waves } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import PublicWhatsNewTeaser from '../whatsnew/PublicWhatsNewTeaser';

interface SeoTeaser {
  title: string;
  summary: string;
  score: number;
}

interface SeoStats {
  totalSpots: number;
  nearWater: number;
  withView: number;
  utmark: number;
  highestScore: number;
  wildlifeObservations: number;
}

interface SeoPayload {
  areaLabel: string;
  county: string;
  municipality?: string;
  stats: SeoStats;
  teasers: SeoTeaser[];
  ctaUrl: string;
}

interface FricampingLandingPageProps {
  countySlug: string;
  municipalitySlug?: string;
}

const endpointFor = (countySlug: string, municipalitySlug?: string) => {
  const path = municipalitySlug
    ? `/fricamping/${encodeURIComponent(countySlug)}/${encodeURIComponent(municipalitySlug)}`
    : `/fricamping/${encodeURIComponent(countySlug)}`;
  return `${path}?format=json`;
};

const StatTile: React.FC<{ label: string; value: string | number; icon: React.ReactNode }> = ({ label, value, icon }) => (
  <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
    <div className="mb-2 text-emerald-300">{icon}</div>
    <div className="text-2xl font-black text-white">{value}</div>
    <div className="mt-1 text-tiny uppercase tracking-wider text-stone-400 font-bold">{label}</div>
  </div>
);

const FricampingLandingPage: React.FC<FricampingLandingPageProps> = ({ countySlug, municipalitySlug }) => {
  const { t } = useTranslation();
  const [payload, setPayload] = useState<SeoPayload | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await fetch(endpointFor(countySlug, municipalitySlug), {
          headers: { Accept: 'application/json' }
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setPayload(data as SeoPayload);
      } catch (err) {
        if (!cancelled) setError('Kunne ikke laste Fricamping-side akkurat nå.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [countySlug, municipalitySlug]);

  const title = useMemo(() => {
    if (!payload) return 'Fricamping i Norge | Fricamp';
    return `Fricamping i ${payload.areaLabel} | Fricamp`;
  }, [payload]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#07110d] text-white flex items-center justify-center">
        <div className="flex items-center gap-3 text-sm font-bold uppercase tracking-wider text-emerald-300">
          <Loader2 className="animate-spin" size={18} />
          {t("auto.seo.fricampinglandingpag.laster_fricamping_data", "Laster fricamping-data")}
        </div>
      </div>
    );
  }

  if (error || !payload) {
    return (
      <div className="min-h-screen bg-[#07110d] text-white flex items-center justify-center p-6 text-center">
        <div>
          <h1 className="text-xl font-black">Fricamp</h1>
          <p className="mt-2 text-stone-300">{error || 'Ingen data tilgjengelig.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#07110d] text-white">
      <main className="mx-auto max-w-5xl px-4 py-10">
        <section className="rounded-3xl border border-emerald-500/20 bg-gradient-to-br from-emerald-950/50 to-black/40 p-6 md:p-8">
          <p className="text-tiny font-black uppercase tracking-widest text-emerald-300">{t("auto.seo.fricampinglandingpag.programmatisk_seo", "Programmatisk SEO")}</p>
          <h1 className="mt-2 text-3xl md:text-4xl font-black">{title}</h1>
          <p className="mt-3 text-stone-300 max-w-2xl">
            Fricamp har analysert {payload.stats.utmark} lovlige plasser for bil og telt i {payload.areaLabel}.
            Nøyaktige koordinater er tilgjengelig i appen.
          </p>
          <a
            href={payload.ctaUrl}
            className="mt-6 inline-flex items-center rounded-xl bg-emerald-500 px-5 py-3 text-sm font-black uppercase tracking-widest text-black hover:bg-emerald-400 transition-colors"
          >
            {t("auto.seo.fricampinglandingpag.logg_inn_gratis_for_kart_og_gp", "Logg inn gratis for kart og GPS")}
          </a>
        </section>

        <section className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3">
          <StatTile label="Analyserte plasser" value={payload.stats.totalSpots} icon={<Mountain size={18} />} />
          <StatTile label="Ved vann" value={payload.stats.nearWater} icon={<Waves size={18} />} />
          <StatTile label="Med utsikt" value={payload.stats.withView} icon={<Eye size={18} />} />
          <StatTile label="Utmark" value={payload.stats.utmark} icon={<ShieldCheck size={18} />} />
          <StatTile label="Høyeste AI-score" value={`${payload.stats.highestScore}/10`} icon={<Trees size={18} />} />
          <StatTile label="Dyrelivsobservasjoner" value={payload.stats.wildlifeObservations} icon={<Trees size={18} />} />
        </section>

        <section className="mt-8 rounded-3xl border border-white/10 bg-black/20 p-6">
          <h2 className="text-lg font-black">{t("auto.seo.fricampinglandingpag.anonyme_plass_eksempler", "Anonyme plass-eksempler")}</h2>
          <div className="mt-4 grid md:grid-cols-2 gap-3">
            {payload.teasers.map((teaser, index) => (
              <article key={`${teaser.title}-${index}`} className="rounded-xl border border-white/10 bg-white/5 p-4">
                <p className="text-sm font-black">{teaser.title}</p>
                <p className="mt-1 text-xs text-stone-300">{teaser.summary}</p>
                <p className="mt-2 text-tiny font-black uppercase tracking-wider text-amber-300">AI-score {teaser.score}/10</p>
              </article>
            ))}
          </div>
        </section>

        <PublicWhatsNewTeaser className="mt-8" />
      </main>
    </div>
  );
};

export default FricampingLandingPage;
